import styles from 'css/src/components/video-planner/VideoDraftLoadingProgress.module.scss';

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ProgressBar from 'js/src/components/common/ui/progress-bar';
import { getVideoDraftById } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';

interface VideoDraftLoadingProgressProps {
    videoDraftId: string;
}

export const VideoDraftLoadingProgress: React.FC<VideoDraftLoadingProgressProps> = ({
    videoDraftId,
}) => {
    const videoDraft = useSelector((state: RootState) => getVideoDraftById(state, videoDraftId));
    const luminary = videoDraft?.luminary;

    const [loadingStartTime] = useState(() => Date.now());
    const [progress, setProgress] = useState(0);

    const isGenerating = videoDraft?.isGenerating ?? false;
    const hasScenes = (luminary?.scenes.length ?? 0) > 0;

    // Use the estimated loading duration from the video draft
    const estimatedDurationSeconds = videoDraft?.estimatedLoadingDurationSeconds || 30;
    const estimatedDurationMs = estimatedDurationSeconds * 1000;

    useEffect(() => {
        if (!isGenerating) {
            setProgress(100);
            return;
        }

        const calculateProgress = (): number => {
            const elapsed = Date.now() - loadingStartTime;
            const progress = (elapsed / estimatedDurationMs) * 100;

            if (progress > 85) {
                // slow down progress after 85%
                const excessProgress = progress - 85;
                const slowedProgress = 85 + excessProgress * 0.3;
                // cap at 97% to avoid going over
                return Math.min(slowedProgress, 97);
            }

            return progress;
        };

        const interval = setInterval(() => {
            const newProgress = calculateProgress();
            setProgress(newProgress);
        }, 100);

        return () => clearInterval(interval);
    }, [isGenerating, hasScenes, loadingStartTime, estimatedDurationMs]);

    if (!videoDraft || !luminary || !isGenerating) {
        return null;
    }

    return (
        <div
            className={`${styles['video-draft-loading']} mt-l5-8`}
            data-testid="video-draft-loading-progress"
        >
            <ProgressBar current={progress} total={100} />
        </div>
    );
};
